import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { ApiClient } from "./api.js";
import { basePath } from "./session.js";
import type { AuthMe, VerticalProfile } from "./types.js";

interface TenantContextValue {
  api: ApiClient;
  tenantSlug: string;
  me?: AuthMe;
  profile?: VerticalProfile;
  loading: boolean;
  error?: Error;
  reload: () => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export interface TenantProviderProps {
  tenantSlug: string;
  apiBaseUrl?: string;
  actorId?: string;
  children: ReactNode;
  /** Test seam — supply a pre-built ApiClient with a custom fetcher. */
  api?: ApiClient;
}

export function TenantProvider({ tenantSlug, apiBaseUrl, actorId, children, api: providedApi }: TenantProviderProps): React.JSX.Element {
  const navigate = useNavigate();

  const api = useMemo(() => {
    const onUnauthorized = (): void => {
      // Cookie expired or revoked — bounce through the SSO start endpoint so
      // the user re-authenticates against the CRM and lands back here.
      const prefix = basePath();
      const next = typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : "/";
      window.location.href = `${prefix}/api/v1/auth/sso/start?return=${encodeURIComponent(next)}`;
    };
    if (providedApi) return providedApi.withTenant(tenantSlug);
    return new ApiClient({ baseUrl: apiBaseUrl, tenantSlug, actorId, onUnauthorized });
  }, [providedApi, apiBaseUrl, tenantSlug, actorId, navigate]);

  const [me, setMe] = useState<AuthMe | undefined>();
  const [profile, setProfile] = useState<VerticalProfile | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    Promise.all([api.authMe(tenantSlug), api.verticalProfile(tenantSlug)])
      .then(([nextMe, nextProfile]) => {
        if (cancelled) return;
        setMe(nextMe);
        setProfile(nextProfile);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, tenantSlug, reloadKey]);

  const value: TenantContextValue = {
    api,
    tenantSlug,
    me,
    profile,
    loading,
    error,
    reload: () => setReloadKey((n) => n + 1)
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within <TenantProvider>");
  return ctx;
}
